import { useMemo, useState } from 'react';

import { Stack, Tab, TabList, Tabs, Checkbox, HStack, TabPanels, TabPanel, Skeleton, Text, Switch } from '@ecoinvent/ui';
import { useParams } from 'react-router';

import { ElementaryExchange, IntermediateExchange } from 'hooks/data/datasets/exchanges/types';
import useVersionInfo from 'hooks/useVersionInfo';

import { elementaryCheckboxes } from './checkboxes';
import ExchangeInput from './ExchangeInput';
import ExchangeList from './ExchangeList';
import { ExchangeFilter } from './types';
import { loadShowComments, saveShowComments } from './utils';

type Props = {
  intermediateExchange: IntermediateExchange[];
  elementaryExchange: ElementaryExchange[];
  isLoading?: boolean;
};

const ExchangeView = ({ intermediateExchange, isLoading, elementaryExchange }: Props) => {
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showComments, setShowComments] = useState(loadShowComments());
  const { spold_id } = useParams();

  const [enabledFeatures, setEnabledFeatures] = useState<ExchangeFilter[]>(['To environment', 'From environment']);

  const { ver, system_model } = useVersionInfo();
  const baseUrl = `/${ver}/${system_model}/dataset/`;

  const onChangeTab = (newValue: number) => {
    if ([0, 1].includes(activeTab)) setActiveTab(newValue);
  };

  const handleToggle = (value: ExchangeFilter) => () => {
    const currentIndex = enabledFeatures.indexOf(value);
    const newChecked = [...enabledFeatures];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setEnabledFeatures(newChecked);
  };

  const handleShowComments = (value: boolean) => {
    setShowComments(value);
    saveShowComments(value);
  };

  const filteredExchanges = useMemo(() => {
    const exchanges = activeTab === 0 ? intermediateExchange : elementaryExchange;
    const key = `${ver}_${system_model}_${spold_id}`;

    const filtered = exchanges
      .map((exc, idx) => ({ ...exc, key: `${key}_${exc.group.replaceAll(' ', '_')}_${idx}` }))
      .filter(
        (exc: any) =>
          exc.name.toLowerCase().includes(searchText.toLowerCase()) ||
          exc.link?.name.toLowerCase().includes(searchText.toLowerCase()) ||
          exc.link?.geography.toLowerCase().includes(searchText.toLowerCase())
      )
      .filter((exc) => (activeTab === 0 ? true : enabledFeatures.includes(exc.group as ExchangeFilter)));

    return filtered;
  }, [activeTab, elementaryExchange, intermediateExchange, searchText, enabledFeatures, spold_id, system_model, ver]);

  const checkboxesToRender = activeTab > 0 ? elementaryCheckboxes : [];

  const elementaryExchangesTabLabel = `${
    filteredExchanges.length < elementaryExchange.length && activeTab !== 0 ? `${filteredExchanges.length} of ` : ''
  }${elementaryExchange.length}`;

  return (
    <Stack direction={'row'} spacing={6}>
      <Stack w={'100%'} spacing={2}>
        <Stack>
          <Skeleton w="100%" isLoaded={!isLoading} alignSelf={'flex-start'}>
            <Stack
              w={'100%'}
              direction={{ base: 'column', lg: 'row' }}
              alignItems={{ base: 'stretch', lg: 'center' }}
              spacing={3}
              justify={'flex-start'}
            >
              <ExchangeInput
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Filter exchanges..."
              />
              <Stack flexDir="row" spacing={4} p={4} alignItems={'center'}>
                <Switch
                  isChecked={showComments}
                  colorScheme="green"
                  size="md"
                  onChange={() => handleShowComments(!showComments)}
                />
                <Text whiteSpace="nowrap">Show comments</Text>
              </Stack>
            </Stack>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} alignSelf={'flex-end'}>
            <HStack spacing={4}>
              {checkboxesToRender.map((checkbox) => (
                <Checkbox
                  key={checkbox.key}
                  onChange={handleToggle(checkbox.key)}
                  isChecked={enabledFeatures.includes(checkbox.key)}
                >
                  {checkbox.name}
                </Checkbox>
              ))}
            </HStack>
          </Skeleton>
        </Stack>
        <Tabs isLazy index={activeTab} onChange={onChangeTab} w={'100%'}>
          <Skeleton isLoaded={!isLoading}>
            <TabList>
              <Tab fontSize={'sm'}>Intermediate ({intermediateExchange.length})</Tab>
              <Tab fontSize={'sm'}>Elementary ({elementaryExchangesTabLabel})</Tab>
            </TabList>
          </Skeleton>

          <TabPanels>
            <TabPanel p={0}>
              <ExchangeList
                exchanges={filteredExchanges}
                baseUrl={baseUrl}
                isLoading={isLoading}
                showComments={showComments}
                isFirstList
              />
            </TabPanel>
            <TabPanel p={0}>
              <ExchangeList exchanges={filteredExchanges} baseUrl={baseUrl} isLoading={isLoading} showComments={showComments} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>
    </Stack>
  );
};

export default ExchangeView;
